import { Controller, useFormContext } from "react-hook-form";
import Chevron from "../../../assets/icons/Chevron";
import { InputBaseParams } from "../Form";

export type DropdownOption = {
  label?: string;
  value: string | number;
  className?: string;
  disabled?: boolean;
};

// We can use regex to apply only the padding based classes to the
// select element (to keep the apperance that the dropdown is one element)
const paddingRegex = new RegExp("^(p-|p.-)");

export const Dropdown = ({
  placeholder,
  value,
  options,
  className,
  onChange
}: {
  placeholder?: string;
  options: DropdownOption[];
  value: string;
  className?: string;
  onChange: (value: string) => void;
}) => (
  <article className={"relative " + className}>
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className={`
        flex-grow appearance-none rounded-md border-[1px] border-ctb-grey-700 
        !bg-ctb-blue-600 text-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-ctb-blue-300 
        ${value === " " ? "text-gray-400" : "bg-white"}
      `}
    >
      <option
        value=""
        disabled
        className="bg-white text-black"
      >
        {placeholder}
      </option>
      {options.map((option, index) => {
        return (
          <option key={index} value={option.value} className={option.className || "bg-white text-black"}>
            {option.label || option.value}
          </option>
        );
      })}
    </select>

    <Chevron className="absolute right-3 top-0 z-0 h-full w-5 stroke-white" />
  </article>
);

/**
 * A controlled HTML select element
 * (i.e. dropdown input) for forms
 */
const ControlledDropdown = ({
  name,
  placeholder,
  options,
  rules,
  className,
  errorClassName,
  defaultValue
}: InputBaseParams & {
  placeholder?: string;
  options: DropdownOption[];
  className?: string;
  defaultValue?: string | number; // Add defaultValue here
}) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue} // Set default value here
      render={({ field, fieldState: { error } }) => (
        <div
          className={
            "relative flex items-center " +
            (className
              ?.split(" ")
              .filter(c => !paddingRegex.test(c))
              .join(" ") || "") +
            " " +
            (error && errorClassName)
          }
        >
          <select
            {...field}
            className={
              "relative z-10 w-full appearance-none rounded-none bg-transparent " +
              (className
                ?.split(" ")
                .filter(c => paddingRegex.test(c))
                .join(" ") || "") +
              (!field.value || field.value === "" ? " text-gray-400 " : "")
            }
          >
            {placeholder && <option value="">{placeholder}</option>}
            {options.map((option, index) => (
              <option
                key={index}
                value={String(option.value)}
                disabled={option.disabled}
                className={option.className || "bg-white text-black"}
              >
                {option.label ?? option.value}
              </option>
            ))}
          </select>

          <Chevron
            className={`absolute right-4 z-0 w-5 ${error ? "stroke-red-500" : "stroke-gray-700"
              }`}
          />
        </div>
      )}
    />
  );
};


export default ControlledDropdown;
